import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import codepointsJson from '../lib/material-icon-codepoints.json'

// Material Icons (outlined) picker with autocomplete + live preview.
// Powers the Categories admin's home-tile icon field. Renders any of the
// ~2278 icons via the `material-icons` npm package's outlined WOFF font
// (imported once in main.tsx). Names match the Flutter customer app's
// Icons.<name>_outlined + IconData(codepoint, fontFamily:
// 'MaterialIconsOutlined') so admin preview == customer render.
//
// UX:
//  - Selected icon shows large on the left with its name below
//  - Search input on the right filters the grid; results ranked with
//    prefix-match first, then substring
//  - Grid caps at MAX_RESULTS to keep the DOM light; typing narrows
//  - Clicking a tile sets the value; the X button clears

const ALL_ICON_NAMES = Object.keys(codepointsJson as Record<string, string>).sort()
const MAX_RESULTS = 60

function rankMatches(query: string, all: string[]): string[] {
  if (!query) return all.slice(0, MAX_RESULTS)
  const q = query.trim().toLowerCase().replace(/[^a-z0-9]/g, '_')
  if (!q) return all.slice(0, MAX_RESULTS)
  const prefix: string[] = []
  const substring: string[] = []
  for (const name of all) {
    if (name.startsWith(q)) prefix.push(name)
    else if (name.includes(q)) substring.push(name)
    if (prefix.length + substring.length >= MAX_RESULTS * 2) break
  }
  return [...prefix, ...substring].slice(0, MAX_RESULTS)
}

interface Props {
  value: string | null
  onChange: (name: string | null) => void
  disabled?: boolean
}

export default function IconPicker({ value, onChange, disabled }: Props) {
  const [query, setQuery] = useState('')

  const results = useMemo(() => rankMatches(query, ALL_ICON_NAMES), [query])

  const clearable = !!value && !disabled

  return (
    <div className={`space-y-3 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex items-center gap-4">
        {/* Preview */}
        <div className="flex-none w-16 h-16 rounded-lg border border-[#3a3a3a] bg-[#242424] flex items-center justify-center">
          {value ? (
            <span className="material-icons-outlined text-white" style={{ fontSize: 32 }}>
              {value}
            </span>
          ) : (
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">None</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-mono truncate">{value ?? '— pick an icon —'}</div>
          <div className="text-[11px] text-gray-500">
            {value
              ? 'Preview above shows exactly how the customer app renders it.'
              : 'Search below, then click an icon to select.'}
          </div>
          {clearable && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-white"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Search input */}
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search icons (e.g. wine, coffee, star)…"
          className="w-full pl-8 pr-8 py-2 text-sm rounded-lg border border-[#3a3a3a] bg-[#242424] text-white placeholder:text-gray-600 focus:outline-none focus:border-[#C41E3A]"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Results grid */}
      <div className="max-h-72 overflow-y-auto rounded-lg border border-[#3a3a3a] bg-[#1a1a1a] p-2">
        {results.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-6">No icons match &ldquo;{query}&rdquo;.</p>
        ) : (
          <div className="grid grid-cols-6 gap-1.5">
            {results.map(name => {
              const active = name === value
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => onChange(name)}
                  title={name}
                  className={`aspect-square rounded-md flex flex-col items-center justify-center gap-0.5 transition-colors ${
                    active
                      ? 'bg-[#C41E3A]/20 border border-[#C41E3A]'
                      : 'bg-[#242424] hover:bg-[#2e2e2e] border border-transparent'
                  }`}
                >
                  <span className="material-icons-outlined text-white" style={{ fontSize: 22 }}>{name}</span>
                  <span className="text-[8px] text-gray-500 truncate w-full text-center px-0.5">{name}</span>
                </button>
              )
            })}
          </div>
        )}
        {results.length === MAX_RESULTS && !query && (
          <p className="text-[10px] text-gray-600 text-center mt-2">
            Showing first {MAX_RESULTS} of {ALL_ICON_NAMES.length}. Type to narrow.
          </p>
        )}
      </div>
    </div>
  )
}
